// @ts-strict-ignore
import { getActionName } from 'flow/service/actions';
import { ActionType, WorkflowTransformationIssueType, booleanRuleStrings } from '../../constants';
import type {
  Action, ActionOption, Attribute, AttributeComponent, DataRoute, Expression,
  LegacyAttributeComponent,
  List, ObjectIdTransformationMap, ReasonCodeGroup, Rules, ServicesByName, Threshold,
} from '../../types';
import { generatePublicIdHash, remapExpressionDataRouteIds, remapListIdsForListServiceAction } from '../../utils';
import { getAttributeObject } from './utils';

/**
 * remap/update threshold ids in boolean rule objects
 * based on thresholdPublicIdReassignmentMap because threshold public ids are reassigned
 *
 * @param thresholdPublicIdReassignmentMap a map object that has the old threshold public id as the key and the reassigned new public id as the value
 */
export function remapBooleanRuleIds({
  ruleItem,
  thresholdPublicIdReassignmentMap,
}) {
  if (ruleItem?.id) {
    // the rule item is indexedrule type, which only has one id
    if (thresholdPublicIdReassignmentMap[ruleItem.id]) {
      ruleItem.id = thresholdPublicIdReassignmentMap[ruleItem.id];
    }
    return ruleItem;
  }

  const operatorArray = ruleItem.and || ruleItem.or;
  if (operatorArray?.length) {
    // if rule item has and/or key property, the value of it is always an array
    // therefore we need to recursively iterate and remap in there
    const operatorKey = ruleItem.and
      ? booleanRuleStrings.AND_OPERATOR_STRING
      : booleanRuleStrings.OR_OPERATOR_STRING;
    return {
      [operatorKey]: operatorArray.map(item => remapBooleanRuleIds({
        ruleItem: item,
        thresholdPublicIdReassignmentMap,
      })),
    };
  }

  return {};
}

function remapMLAComponentIds({
  matchingAttributeComponents,
  incomingAttributeComponents,
}: {
  matchingAttributeComponents: Array<LegacyAttributeComponent>;
  incomingAttributeComponents: Array<AttributeComponent>;
}) {
  return incomingAttributeComponents.map(attributeComponent => {
    const matchingAttrComponentByPlaceholder = matchingAttributeComponents.find(attrComp => (
      attrComp.placeholder === attributeComponent.attributeComponent?.placeholder
    ));

    if (matchingAttrComponentByPlaceholder) {
      attributeComponent.attributeComponent
        .attributeComponentId = matchingAttrComponentByPlaceholder.attribute_component_id;
    }

    return attributeComponent;
  });
}

/**
 * remap/reassign threshold and data route public ids
 * as well as remap/update other usages of public ids (action id, etc) and numerical ids
 * as part of paste import processing
 *
 * @param serviceAttributeData provided from the pasted data
 * @param reasonCodeGroupData provided from the pasted data
 * @param actionPublicIdReassignmentMap a map object that has the old action public id as the key and the reassigned new public id as the value
 * @param updatedActionsForImport an updated set of actions after pre-processing - key-value pair
 * @param rulesPendingMerge the boolean rule from new incoming (pasted) action that needs to be merged later
 * @param expressionsPendingMerge the output attr expression from new incoming (pasted) action that needs to be merged later
 */
export function remapIdsForThresholdsAndDataRoutes({
  thresholds,
  dataRoutes,
  services,
  serviceAttributeData,
  reasonCodeGroups,
  reasonCodeGroupData,
  actionPublicIdReassignmentMap,
  updatedActionsForImport,
  rulesPendingMerge,
  expressionsPendingMerge,
}: {
  thresholds: [string, Threshold][];
  dataRoutes: [string, DataRoute][];
  services: ServicesByName;
  serviceAttributeData: Record<number, {
    service: string;
    attribute: Attribute;
  }>;
  reasonCodeGroups: ReasonCodeGroup[];
  reasonCodeGroupData: Record<number, ReasonCodeGroup>;
  actionPublicIdReassignmentMap: ObjectIdTransformationMap;
  updatedActionsForImport: Record<string, Action>;
  rulesPendingMerge?: Record<string, Rules>;
  expressionsPendingMerge?: Record<string, Expression>;
}) {
  const issuesWithIncomingThresholds = [];
  const identifiedAttributesMissingInTheOrganization = [];
  const thresholdsWithReassignedPublicId = {};
  const thresholdPublicIdReassignmentMap = {};
  if (thresholds.length) {
    thresholds.forEach(([thresholdKey, thresholdValue]) => {
      const newPublicId = `threshold__${generatePublicIdHash(20)}`;
      thresholdPublicIdReassignmentMap[thresholdKey] = newPublicId;
      thresholdValue.publicId = newPublicId; // assign a new public id

      // remap with the new public id of this action
      thresholdValue.actionId = actionPublicIdReassignmentMap[thresholdValue.actionId];
      thresholdValue.left.actionId = actionPublicIdReassignmentMap[
        thresholdValue.left.actionId];

      // right action may or may not exist
      const oldPublicIdForRightAction = thresholdValue.right?.actionId;
      if (oldPublicIdForRightAction) {
        thresholdValue.right.actionId = actionPublicIdReassignmentMap[
          oldPublicIdForRightAction];
      }

      const targetAction = updatedActionsForImport[thresholdValue.actionId]; // get the updated action object
      // if left or right action attribute was specified, we need to get a new correct attribute id
      // because attribute_id could be different between orgs/cells
      // getAttributeObject internally uses attribute_token if available to find the matching attribute

      const leftAttrId = thresholdValue.left.attributeId;
      if (leftAttrId) {
        const matchingAttribute = getAttributeObject({
          serviceAttributeData,
          services,
          attributeId: leftAttrId,
        });

        if (matchingAttribute?.attribute_id) {
          thresholdValue.left.attributeId = matchingAttribute.attribute_id;
          if (thresholdValue.left.attributeComponents.length > 0
            && matchingAttribute.is_multilevel) {
            thresholdValue.left.attributeComponents = remapMLAComponentIds({
              matchingAttributeComponents: matchingAttribute.components,
              incomingAttributeComponents: thresholdValue.left.attributeComponents,
            });
          }
        } else {
          // if matching one not found, we add to the missing items list
          const originalAttribute = serviceAttributeData[thresholdValue.left.attributeId]
            ?.attribute;
          identifiedAttributesMissingInTheOrganization.push({
            type: 'attribute',
            name: originalAttribute?.display_name,
            status: 'unavailable',
            data: {
              action: targetAction ? getActionName(targetAction) : '',
              originalAttribute,
            },
          });
        }
      }

      const rightAttrId = thresholdValue.right.attributeId;
      if (rightAttrId) {
        const matchingAttribute = getAttributeObject({
          serviceAttributeData,
          services,
          attributeId: rightAttrId,
        });

        if (matchingAttribute?.attribute_id) {
          thresholdValue.right.attributeId = matchingAttribute.attribute_id;
          if (thresholdValue.right.attributeComponents.length > 0
            && matchingAttribute.is_multilevel) {
            thresholdValue.right.attributeComponents = remapMLAComponentIds({
              matchingAttributeComponents: matchingAttribute.components,
              incomingAttributeComponents: thresholdValue.right.attributeComponents,
            });
          }
        } else {
          // if matching one not found, we add to the missing items list
          const originalAttribute = serviceAttributeData[thresholdValue.right.attributeId]
            ?.attribute;
          identifiedAttributesMissingInTheOrganization.push({
            type: 'attribute',
            name: originalAttribute?.display_name,
            status: 'unavailable',
            data: {
              action: targetAction ? getActionName(targetAction) : '',
              originalAttribute,
            },
          });
        }
      }

      // if right reason code group was specified, we need to get a new correct group id
      // we use name as the unique identifier to do the comparison for reason code groups so far
      const rightReasonCodeGroupId = thresholdValue.right.reasonCodeGroupId;
      if (rightReasonCodeGroupId) {
        const originalReasonCodeGroup = reasonCodeGroupData[rightReasonCodeGroupId];
        const matchingReasonCodeGroup = originalReasonCodeGroup
          ? reasonCodeGroups.find(
            reasonCodeGroup => reasonCodeGroup.name.toLowerCase()
              === originalReasonCodeGroup.name.toLowerCase(),
          )
          : undefined;

        if (matchingReasonCodeGroup && !matchingReasonCodeGroup?.archived) {
          thresholdValue.right.reasonCodeGroupId = matchingReasonCodeGroup.id;
        } else {
          // if matching one not found, we add to the missing items list
          const identifiedRCG = identifiedAttributesMissingInTheOrganization.findIndex(
            obj => obj.name === originalReasonCodeGroup.name);

          if (identifiedRCG > -1) {
            identifiedAttributesMissingInTheOrganization[identifiedRCG].data = [
              ...identifiedAttributesMissingInTheOrganization[identifiedRCG].data,
              {
                threshold: newPublicId,
                originalReasonCodeGroup,
              },
            ];
          } else {
            identifiedAttributesMissingInTheOrganization.push({
              type: 'reasoncodegroup',
              name: `${originalReasonCodeGroup?.name}`,
              status: '',
              data: [{
                threshold: newPublicId,
                originalReasonCodeGroup,
              }],
            });
          }
        }
      }

      let issueType;
      const leftAction = updatedActionsForImport[thresholdValue.left.actionId];
      if (
        leftAction?.type === ActionType.TAG
        && thresholdValue.right.attributeValue === '0.0'
        && thresholdValue.right.leftTagActionResult !== null
      ) {
        issueType = WorkflowTransformationIssueType
          .INCORRECT_THRESHOLD_RIGHT_ATTR_VALUE_LEFTACTION_TAG;
        thresholdValue.right.attributeValue = null;
      }
      if (
        leftAction?.type === ActionType.OUTCOME_REASON
          && thresholdValue.right.leftTagActionResult !== null
      ) {
        issueType = WorkflowTransformationIssueType
          .INCORRECT_THRESHOLD_RIGHT_ATTR_VALUE_LEFTACTION_OUTCOMEREASON;
        if (thresholdValue.right.leftTagActionResult === true) {
          thresholdValue.right.attributeValue = 'true';
        } else if (thresholdValue.right.leftTagActionResult === false) {
          thresholdValue.right.attributeValue = 'false';
        }
        thresholdValue.right.leftTagActionResult = null;
      }

      if (issueType) {
        issuesWithIncomingThresholds.push({
          publicId: newPublicId,
          manualResolution: false,
          type: issueType,
          data: thresholdValue,
        });
      }

      // add the threshold value object to the new object with a new public id as the property key
      thresholdsWithReassignedPublicId[newPublicId] = thresholdValue;
    });
  }

  const dataRoutesWithReassignedPublicId = {};
  const dataRoutePublicIdReassignmentMap = {};
  if (dataRoutes.length) {
    dataRoutes.forEach(([dataRouteKey, dataRouteValue]) => {
      const newPublicId = `dataroute__${generatePublicIdHash(20)}`;
      dataRoutePublicIdReassignmentMap[dataRouteKey] = newPublicId;
      dataRouteValue.publicId = newPublicId; // assign a new public id

      // remap with the new public id of this action
      dataRouteValue.sourceId = actionPublicIdReassignmentMap[dataRouteValue.sourceId];
      dataRouteValue.targetId = actionPublicIdReassignmentMap[dataRouteValue.targetId];

      const targetAction = updatedActionsForImport[dataRouteValue.sourceId];
      const attrId = dataRouteValue.attribute?.id;
      if (attrId) {
        // if attribute was specified, we need to get a new correct attribute id
        // because attribute_id could be different between orgs/cells
        // getAttributeObject internally uses attribute_token if available to find the matching attribute
        const matchingAttribute = getAttributeObject({
          serviceAttributeData,
          services,
          attributeId: attrId,
        });

        if (matchingAttribute?.attribute_id) {
          dataRouteValue.attribute.id = matchingAttribute.attribute_id;
          if (dataRouteValue.attribute.multiLevelAttributeFilter?.filterComponents?.length > 0
            && matchingAttribute.is_multilevel) {
            dataRouteValue.attribute.multiLevelAttributeFilter
              .filterComponents = remapMLAComponentIds({
                matchingAttributeComponents: matchingAttribute.components,
                incomingAttributeComponents: dataRouteValue.attribute.multiLevelAttributeFilter
                  .filterComponents,
              });
          }
        } else {
          const originalAttribute = serviceAttributeData[dataRouteValue.attribute.id]
            ?.attribute;
          identifiedAttributesMissingInTheOrganization.push({
            type: 'attribute',
            name: originalAttribute?.display_name,
            status: 'unavailable',
            data: {
              action: targetAction ? getActionName(targetAction) : '',
              originalAttribute,
            },
          });
        }
      }

      // add the data route value object to the new object with a new public id as the property key
      dataRoutesWithReassignedPublicId[newPublicId] = dataRouteValue;
    });
  }


  const updatedActionsForImportWithRemappedRuleExpressions: Record<string, Action> = {};
  Object.entries(updatedActionsForImport).forEach(([actionKey, actionValue]) => {
    // remap threshold ids in the boolean rules of each action if this has rules
    // but this remapping only is applied if there's no rulesPendingMerge OR rulesPendingMerge object does not contain any item under an action's public id
    // this is because the deep merging logic that will be executed after this remapIdsForThresholdsAndDataRoutes function step
    // will use both existing rules and new incoming rules and merge them together by doing comparison
    // and remapping existing rules' threshold ids entirely before such deep merging logic execution would lead to the deep merging comparison
    // not being able to get the threshold value as the id was remapped. you either need to store that remapped data again and pass that down as another 2nd remapped list
    // but we can simply choose to keep ids same here. (the deep merging would use the existing thresholds and newly incoming thresholds both, and existing thresholds do not need remapping if unchanged)
    if (actionValue.rules && (!rulesPendingMerge || !rulesPendingMerge?.[actionKey])) {
      actionValue.rules = remapBooleanRuleIds({
        ruleItem: actionValue.rules,
        thresholdPublicIdReassignmentMap,
      });
    }

    // remap data route ids in the expression of each action if this is output attribute
    // but this remapping only is applied if there's no expressionsPendingMerge OR expressionsPendingMerge object does not contain any item under an action's public id
    // they'll be handled later with the deep merging logic. check the comment above for threshold id remapping for the reason as the deep merging for output expression is very similar
    if (actionValue.type === 'output' && actionValue.output?.expression
      && (!expressionsPendingMerge || !expressionsPendingMerge?.[actionKey])) {
      actionValue.output.expression = remapExpressionDataRouteIds({
        expressionArray: actionValue.output.expression,
        dataRouteIdMap: dataRoutePublicIdReassignmentMap,
      });
    }

    updatedActionsForImportWithRemappedRuleExpressions[actionKey] = actionValue;
  });

  const rulesPendingMergeWithRemappedId = {};
  if (rulesPendingMerge) {
    // these can be remapped and should be remapped as these would contain new rule thresholds
    // deep merging comes later
    Object.entries(rulesPendingMerge).forEach(
      ([ruleId, ruleValue]) => {
        rulesPendingMergeWithRemappedId[ruleId] = remapBooleanRuleIds({
          ruleItem: ruleValue,
          thresholdPublicIdReassignmentMap,
        });
      });
  }

  const expressionsPendingMergeWithRemappedId = {};
  if (expressionsPendingMerge) {
    // these can be remapped and should be remapped as these would contain new expression data routes
    // deep merging comes later
    Object.entries(expressionsPendingMerge).forEach(
      ([expressionActionId, expressionValue]) => {
        expressionsPendingMergeWithRemappedId[expressionActionId] = remapExpressionDataRouteIds({
          expressionArray: expressionValue,
          dataRouteIdMap: dataRoutePublicIdReassignmentMap,
        });
      });
  }

  return {
    updatedActionsForImportWithRemappedRuleExpressions,
    thresholdsWithReassignedPublicId,
    dataRoutesWithReassignedPublicId,
    rulesPendingMergeWithRemappedId,
    expressionsPendingMergeWithRemappedId,
    identifiedAttributesMissingInTheOrganization,
    issuesWithIncomingThresholds,
  };
}

/**
 * remap/reassign action option value items
 * such as public ids and also customer_list_id to be pointing to the correct list id after pasting
 *
 */
export function remapActionOptionValues({
  actionOption,
  action,
  lists,
}: {
  actionOption: Record<string, ActionOption>;
  action: Action;
  lists: List[];
}) {
  const issues = [];
  Object.entries(actionOption).forEach(([actionOptionKey, actionOptionValue]) => {
    if (actionOptionKey === 'customer_list_id') {
      // customer list id is all different and if this was copied from a different org/cell/env, we need to remap list id
      const remappedListId = remapListIdsForListServiceAction({
        actionOptionValue,
        action,
        lists,
      });

      // if remappedListId has `value` defined, it returned an updated id
      if (remappedListId.value) {
        actionOptionValue.value = remappedListId.value;
      }

      // if remappedListId has type value defined, that means there's either an issue, or the data was updated correctly
      if (remappedListId.type) {
        issues.push({
          publicId: action.publicId,
          type: remappedListId.type,
          manualResolution: remappedListId.type
            === WorkflowTransformationIssueType.NO_MATCHING_LIST_ID_IN_CUSTOMER,
          data: {
            [actionOptionKey]: actionOptionValue,
          },
        });
      }
    }
    actionOptionValue.publicId = `actionoption__${generatePublicIdHash(20)}`;
  });

  return { actionOption, issues };
}

/**
 * remap thresholds data for actual config data comparison
 * by replacing action ids into names
 */
export function remapThresholdPublicIdRefsIntoNames({
  actions,
  threshold,
}: {
  actions: Record<string, Action>;
  threshold: Threshold;
}) {
  // swap all action public ids into the name of the action
  const thresholdTargetAction = actions[threshold.actionId];
  threshold.actionId = getActionName(thresholdTargetAction);

  const thresholdLeftAction = actions[threshold.left.actionId];
  threshold.left.actionId = getActionName(thresholdLeftAction);

  if (threshold.right.actionId) {
    const thresholdRightAction = actions[threshold.right.actionId];
    threshold.right.actionId = getActionName(thresholdRightAction);
  }

  threshold.publicId = '';
  return threshold;
}

/**
* remap data route item for actual config data comparison
* by replacing target/source action ids into names
*/
export function remapDataRoutePublicIdRefsIntoNames({
  actions,
  dataRoute,
}: {
  actions: Record<string, Action>;
  dataRoute: DataRoute;
}) {
  // swap all action public ids into the name of the action
  const sourceAction = actions[dataRoute.sourceId];
  dataRoute.sourceId = getActionName(sourceAction);

  const targetAction = actions[dataRoute.targetId];
  dataRoute.targetId = getActionName(targetAction);

  // we temporarily set them to 0/'' for comparison
  if (dataRoute.attribute?.id) {
    dataRoute.attribute.id = 0;
  }
  if (dataRoute.attribute?.token) {
    dataRoute.attribute.token = '';
  }

  dataRoute.publicId = '';
  return dataRoute;
}
